import { useMemo, useState, useRef, useEffect, Suspense } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, ContactShadows, Environment, Html, SoftShadows, AccumulativeShadows, RandomizedLight } from "@react-three/drei";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";
import * as THREE from "three";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Sparkles, Ruler, Palette, Send, Check, Smartphone, Cuboid, Bookmark, LogIn } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { studioApi } from "@/lib/studioApi";
import { useStudioAuth } from "@/context/StudioAuthContext";
import { trackEvent } from "@/lib/analytics";

/* ---------- Finish libraries ------------------------------------------------ */

const COUNTERTOPS = [
  { id: "italian-marble", label: "Italian Marble", color: "#F1EDE4", rate: 1850 },
  { id: "carrara",        label: "Carrara White",  color: "#E8E6E1", rate: 1400 },
  { id: "quartz-black",   label: "Quartz Black",   color: "#1E1E1E", rate: 950  },
  { id: "granite",        label: "Kashmir Granite", color: "#B0A88A", rate: 720 },
  { id: "corian",         label: "Corian Bone",    color: "#F4EFE6", rate: 1600 },
  { id: "wood-butcher",   label: "Oak Butcherblock", color: "#B98A4E", rate: 1050 },
];

const CABINETS = [
  { id: "handleless-white",  label: "Handleless White",  color: "#F5F3EE", rate: 2200 },
  { id: "walnut-veneer",     label: "Walnut Veneer",     color: "#6E4A2F", rate: 3200 },
  { id: "matt-graphite",     label: "Matt Graphite",     color: "#2E2E2E", rate: 2800 },
  { id: "sage-lacquer",      label: "Sage Green Lacquer", color: "#7C8A78", rate: 3000 },
  { id: "champagne-shaker",  label: "Champagne Shaker",  color: "#D8CDB9", rate: 3400 },
];

const FLOORS = [
  { id: "oak-plank",      label: "Oak Wide Plank",    color: "#C09367", rate: 320 },
  { id: "italian-tile",   label: "Italian Porcelain", color: "#D5CFC1", rate: 220 },
  { id: "grey-marble",    label: "Grey Marble",       color: "#8E8B84", rate: 480 },
  { id: "walnut-parquet", label: "Walnut Parquet",    color: "#5C3A22", rate: 420 },
];

const WALLS = [
  { id: "warm-limewash", label: "Warm Limewash",  color: "#EDE6D8", rate: 110 },
  { id: "cool-mist",     label: "Cool Mist",      color: "#E4E7EA", rate: 90  },
  { id: "clay-terracotta", label: "Clay Terracotta", color: "#B87757", rate: 130 },
  { id: "brass-panel",   label: "Brass Panelling", color: "#B08B4B", rate: 380 },
];

const LAYOUTS = [
  { id: "l-shape",   label: "L-Shape" },
  { id: "u-shape",   label: "U-Shape" },
  { id: "island",    label: "Island / Parallel" },
];

/* ---------- Procedural PBR textures (canvas-generated) ---------------------- */

// Generic canvas texture helper — returns a THREE.CanvasTexture ready for PBR use
function makeCanvasTexture(size, draw, opts = {}) {
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d");
  draw(ctx, size);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  tex.colorSpace = THREE.SRGBColorSpace;
  if (opts.repeat) tex.repeat.set(opts.repeat[0], opts.repeat[1]);
  return tex;
}

function marbleTexture(base = "#F1EDE4", vein = "#8f8f8f") {
  return makeCanvasTexture(512, (ctx, s) => {
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, s, s);
    // Soft cloudy background
    for (let i = 0; i < 300; i++) {
      ctx.fillStyle = `rgba(210,205,195,${Math.random() * 0.06})`;
      ctx.beginPath();
      ctx.arc(Math.random() * s, Math.random() * s, Math.random() * 60 + 20, 0, Math.PI * 2);
      ctx.fill();
    }
    // Vein strokes
    ctx.strokeStyle = vein;
    ctx.lineWidth = 0.8;
    for (let v = 0; v < 22; v++) {
      ctx.globalAlpha = 0.28 + Math.random() * 0.35;
      ctx.beginPath();
      let x = Math.random() * s;
      let y = Math.random() * s;
      ctx.moveTo(x, y);
      for (let k = 0; k < 60; k++) {
        x += (Math.random() - 0.5) * 30;
        y += (Math.random() - 0.5) * 5 + Math.random() * 8;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  });
}

function woodTexture(base = "#8a5a34", darker = "#5C3A22") {
  return makeCanvasTexture(512, (ctx, s) => {
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 80; i++) {
      const y = i * (s / 80) + (Math.random() - 0.5) * 2;
      ctx.strokeStyle = `rgba(0,0,0,${Math.random() * 0.15 + 0.05})`;
      ctx.lineWidth = Math.random() * 1.4 + 0.3;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.bezierCurveTo(s * 0.33, y + (Math.random() - 0.5) * 6, s * 0.66, y + (Math.random() - 0.5) * 6, s, y);
      ctx.stroke();
    }
    // knots
    for (let k = 0; k < 4; k++) {
      const x = Math.random() * s;
      const y = Math.random() * s;
      const grad = ctx.createRadialGradient(x, y, 1, x, y, 12);
      grad.addColorStop(0, darker);
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, 12, 0, Math.PI * 2);
      ctx.fill();
    }
  }, { repeat: [2, 1] });
}

function graniteTexture(base = "#B0A88A") {
  return makeCanvasTexture(512, (ctx, s) => {
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 5000; i++) {
      ctx.fillStyle = `rgba(${20 + Math.random() * 100},${20 + Math.random() * 90},${20 + Math.random() * 60},${Math.random() * 0.35})`;
      ctx.fillRect(Math.random() * s, Math.random() * s, Math.random() * 2 + 0.4, Math.random() * 2 + 0.4);
    }
  });
}

function subtleFabric(base = "#F5F3EE") {
  return makeCanvasTexture(256, (ctx, s) => {
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 4000; i++) {
      ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.03})`;
      ctx.fillRect(Math.random() * s, Math.random() * s, 1, 1);
    }
  }, { repeat: [3, 3] });
}

/** Pick the right texture generator by swatch id. */
function textureFor(id, color) {
  if (!id) return null;
  if (id.includes("marble") || id === "carrara") return marbleTexture(color, "#7a7a7a");
  if (id === "corian") return marbleTexture(color, "#c9c1b0");
  if (id.includes("walnut") || id === "oak-plank" || id === "oak-butcher" || id === "walnut-parquet") return woodTexture(color, "#3a2416");
  if (id === "granite") return graniteTexture(color);
  if (id === "italian-tile" || id === "grey-marble") return marbleTexture(color, "#666");
  if (id === "matt-graphite") return subtleFabric("#1E1E1E");
  return subtleFabric(color);
}

/* ---------- 3D Scene (PBR) --------------------------------------------------- */

function Cabinet({ position, size, color, textureId }) {
  const map = useMemo(() => textureFor(textureId, color), [textureId, color]);
  const mat = useMemo(() => new THREE.MeshPhysicalMaterial({
    color: map ? "#ffffff" : color,
    map,
    roughness: textureId?.includes("matt") ? 0.85 : 0.42,
    metalness: 0.02,
    clearcoat: textureId?.includes("matt") ? 0 : 0.35,
    clearcoatRoughness: 0.4,
  }), [color, map, textureId]);
  return (
    <mesh position={position} castShadow receiveShadow material={mat}>
      <boxGeometry args={size} />
    </mesh>
  );
}

function Countertop({ position, size, color, textureId }) {
  const map = useMemo(() => textureFor(textureId, color), [textureId, color]);
  const mat = useMemo(() => new THREE.MeshPhysicalMaterial({
    color: map ? "#ffffff" : color,
    map,
    roughness: 0.12,
    metalness: 0.08,
    clearcoat: 0.9,
    clearcoatRoughness: 0.06,
    reflectivity: 0.5,
  }), [color, map]);
  return (
    <mesh position={position} castShadow receiveShadow material={mat}>
      <boxGeometry args={size} />
    </mesh>
  );
}

function Backwall({ w, h, color, textureId }) {
  const map = useMemo(() => textureFor(textureId, color), [textureId, color]);
  const mat = useMemo(() => new THREE.MeshStandardMaterial({
    color: map ? "#ffffff" : color,
    map,
    roughness: textureId === "brass-panel" ? 0.35 : 0.92,
    metalness: textureId === "brass-panel" ? 0.75 : 0.02,
  }), [color, map, textureId]);
  return (
    <mesh position={[0, h / 2, -1.5]} receiveShadow material={mat}>
      <planeGeometry args={[w, h]} />
    </mesh>
  );
}

function Floor({ w, d, color, textureId }) {
  const map = useMemo(() => {
    const t = textureFor(textureId, color);
    if (t) t.repeat.set(4, 4);
    return t;
  }, [textureId, color]);
  const mat = useMemo(() => new THREE.MeshPhysicalMaterial({
    color: map ? "#ffffff" : color,
    map,
    roughness: textureId?.includes("marble") ? 0.2 : 0.55,
    metalness: 0.04,
    clearcoat: textureId?.includes("marble") ? 0.6 : 0.15,
    clearcoatRoughness: 0.2,
  }), [color, map, textureId]);
  return (
    <mesh position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow material={mat}>
      <planeGeometry args={[w * 1.6, d * 1.8]} />
    </mesh>
  );
}

function Faucet() {
  return (
    <group position={[0.6, 0.95, -1.2]}>
      <mesh castShadow>
        <cylinderGeometry args={[0.028, 0.028, 0.4, 20]} />
        <meshPhysicalMaterial color="#C9A24A" metalness={0.95} roughness={0.12} clearcoat={0.8} clearcoatRoughness={0.05} />
      </mesh>
      <mesh position={[0.14, 0.18, 0]} rotation={[0, 0, -Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.022, 0.022, 0.34, 20]} />
        <meshPhysicalMaterial color="#C9A24A" metalness={0.95} roughness={0.12} clearcoat={0.8} />
      </mesh>
      <mesh position={[0.3, 0.18, 0]} castShadow>
        <sphereGeometry args={[0.028, 20, 20]} />
        <meshPhysicalMaterial color="#C9A24A" metalness={0.95} roughness={0.12} clearcoat={0.8} />
      </mesh>
    </group>
  );
}

function Pendant({ x }) {
  return (
    <group position={[x, 2.2, -0.4]}>
      <mesh>
        <cylinderGeometry args={[0.005, 0.005, 0.9, 8]} />
        <meshBasicMaterial color="#222" />
      </mesh>
      <mesh position={[0, -0.45, 0]} castShadow>
        <sphereGeometry args={[0.14, 32, 32]} />
        <meshPhysicalMaterial color="#F8C21C" emissive="#F8C21C" emissiveIntensity={0.9} metalness={0.2} roughness={0.25} clearcoat={0.8} />
      </mesh>
      <pointLight position={[0, -0.45, 0]} intensity={0.9} distance={4} color="#FFDF9E" />
    </group>
  );
}

function KitchenScene({ layout, length, width, cabinetColor, cabinetId, countertopColor, countertopId, floorColor, floorId, wallColor, wallId, sceneRef }) {
  // Simple parametric kitchen. Base cabinets 0.9m tall, wall units 0.75m tall.
  const L = Math.max(2.4, Math.min(6, length * 0.3048)); // ft → m clamp
  const W = Math.max(2.1, Math.min(4.5, width * 0.3048));
  const bases = [];
  const wallUnits = [];
  const counters = [];

  // Main run along back wall (all layouts)
  bases.push({ p: [0, 0.45, -1.15], s: [L, 0.9, 0.6] });
  counters.push({ p: [0, 0.94, -1.15], s: [L + 0.04, 0.05, 0.62] });
  wallUnits.push({ p: [0, 1.9, -1.35], s: [L, 0.75, 0.35] });

  // Return leg (L or U)
  if (layout === "l-shape" || layout === "u-shape") {
    bases.push({ p: [-L / 2 + 0.3, 0.45, -0.4], s: [0.6, 0.9, 1.5] });
    counters.push({ p: [-L / 2 + 0.3, 0.94, -0.4], s: [0.62, 0.05, 1.52] });
  }
  // U-shape second leg
  if (layout === "u-shape") {
    bases.push({ p: [L / 2 - 0.3, 0.45, -0.4], s: [0.6, 0.9, 1.5] });
    counters.push({ p: [L / 2 - 0.3, 0.94, -0.4], s: [0.62, 0.05, 1.52] });
  }
  // Island for the island layout
  if (layout === "island") {
    bases.push({ p: [0, 0.45, 0.6], s: [L * 0.55, 0.9, 0.9] });
    counters.push({ p: [0, 0.94, 0.6], s: [L * 0.55 + 0.04, 0.05, 0.92] });
  }

  return (
    <group ref={sceneRef}>
      <Floor w={L} d={W} color={floorColor} textureId={floorId} />
      <Backwall w={L * 1.8} h={2.8} color={wallColor} textureId={wallId} />
      {bases.map((b, i) => (
        <Cabinet key={`b-${i}`} position={b.p} size={b.s} color={cabinetColor} textureId={cabinetId} />
      ))}
      {wallUnits.map((u, i) => (
        <Cabinet key={`u-${i}`} position={u.p} size={u.s} color={cabinetColor} textureId={cabinetId} />
      ))}
      {counters.map((c, i) => (
        <Countertop key={`c-${i}`} position={c.p} size={c.s} color={countertopColor} textureId={countertopId} />
      ))}
      <Faucet />
      <Pendant x={-0.6} />
      <Pendant x={0.6} />
      <AccumulativeShadows temporal frames={60} alphaTest={0.85} scale={12} position={[0, 0.005, 0]} opacity={0.65}>
        <RandomizedLight amount={6} radius={5} intensity={1.5} ambient={0.5} position={[4, 6, 3]} />
      </AccumulativeShadows>
    </group>
  );
}

/* ---------- Swatch UI ------------------------------------------------------- */

const Swatch = ({ item, active, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`swatch-${item.id}`}
    className={`group flex items-center gap-3 border transition-all duration-200 px-2.5 py-2 ${
      active ? "border-[#143D63] bg-[#143D63]/5" : "border-[#143D63]/15 hover:border-[#143D63]/50"
    }`}
  >
    <span
      className="w-9 h-9 border border-[#0A0A0A]/10 shrink-0"
      style={{ background: item.color }}
    />
    <span className="text-left">
      <span className="block text-[13px] font-semibold text-[#143D63] leading-tight">{item.label}</span>
      <span className="block text-[11px] text-[#143D63]/60">₹{item.rate.toLocaleString("en-IN")} /sqft</span>
    </span>
    {active && <Check size={14} className="ml-auto text-[#8B0000]" />}
  </button>
);

/* ---------- Page ------------------------------------------------------------ */

export default function Configurator() {
  const [layout, setLayout] = useState("l-shape");
  const [length, setLength] = useState(12);   // ft
  const [width, setWidth] = useState(9);
  const [countertop, setCountertop] = useState(COUNTERTOPS[0]);
  const [cabinet, setCabinet] = useState(CABINETS[0]);
  const [floor, setFloor] = useState(FLOORS[0]);
  const [wall, setWall] = useState(WALLS[0]);

  // AR + scene export
  const sceneRef = useRef(null);
  const [arUrl, setArUrl] = useState(null); // blob: URL of exported .glb
  const [arOpen, setArOpen] = useState(false);
  const [arBuilding, setArBuilding] = useState(false);

  // Lock & Inquire form state
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "Kolkata" });
  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);
  const [searchParams] = useSearchParams();
  const refCode = (searchParams.get("ref") || "").toUpperCase().trim();

  // Customer auth for "Save to Shortlist"
  const { customer } = useStudioAuth();
  const [saving, setSaving] = useState(false);
  const [savedNow, setSavedNow] = useState(false);

  // Load Google's <model-viewer> web component once (used inside AR modal)
  useEffect(() => {
    if (document.querySelector('script[data-model-viewer]')) return;
    const s = document.createElement("script");
    s.type = "module";
    s.src = "https://unpkg.com/@google/model-viewer@3.5.0/dist/model-viewer.min.js";
    s.setAttribute("data-model-viewer", "1");
    document.head.appendChild(s);
  }, []);

  const openInAR = async () => {
    if (!sceneRef.current) {
      toast.error("Scene not ready yet — give it a second.");
      return;
    }
    setArBuilding(true);
    try {
      const exporter = new GLTFExporter();
      const gltf = await new Promise((resolve, reject) => {
        exporter.parse(
          sceneRef.current,
          (result) => resolve(result),
          (err) => reject(err),
          { binary: true, embedImages: true, forceIndices: true }
        );
      });
      const blob = new Blob([gltf], { type: "model/gltf-binary" });
      if (arUrl) URL.revokeObjectURL(arUrl);
      const url = URL.createObjectURL(blob);
      setArUrl(url);
      setArOpen(true);
      trackEvent("configurator_ar_open", { event_category: "3d_configurator" });
    } catch (e) {
      toast.error("Couldn't build the AR model. Please refresh and try again.");
    } finally {
      setArBuilding(false);
    }
  };

  useEffect(() => () => { if (arUrl) URL.revokeObjectURL(arUrl); }, [arUrl]);

  /* ------ Live BOM calculation ------ */
  const bom = useMemo(() => {
    const runningFt = layout === "u-shape" ? length + 10 : layout === "l-shape" ? length + 5 : layout === "island" ? length + 6 : length;
    const cabinetsCost = Math.round(runningFt * (cabinet.rate + 250)); // cabinets sold per running ft
    const countertopCost = Math.round(runningFt * 2.1 * countertop.rate);
    const floorSqft = length * width;
    const floorCost = Math.round(floorSqft * floor.rate);
    const wallCost = Math.round((length * 8) * wall.rate * 0.4); // partial backwall coverage
    const install = Math.round((cabinetsCost + countertopCost) * 0.12);
    const total = cabinetsCost + countertopCost + floorCost + wallCost + install;
    return {
      runningFt,
      cabinetsCost,
      countertopCost,
      floorSqft,
      floorCost,
      wallCost,
      install,
      total,
    };
  }, [layout, length, width, cabinet, countertop, floor, wall]);

  const formatINR = (n) =>
    "₹" + n.toLocaleString("en-IN");

  const currentConfig = () => ({
    space: "Modular Kitchen",
    layout: LAYOUTS.find((l) => l.id === layout)?.label,
    layout_id: layout,
    length_ft: length,
    width_ft: width,
    cabinets: cabinet.label,
    cabinet_id: cabinet.id,
    countertop: countertop.label,
    countertop_id: countertop.id,
    flooring: floor.label,
    floor_id: floor.id,
    wall: wall.label,
    wall_id: wall.id,
    estimated_total_inr: formatINR(bom.total),
    total_number: bom.total,
  });

  const lockAndInquire = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone");
      return;
    }
    setSubmitting(true);
    try {
      const config = currentConfig();
      await api.post("/configurator/lock", { ...form, ref: refCode || null, config });
      trackEvent("configurator_lock", { event_category: "3d_configurator", total_inr: bom.total });
      toast.success("Locked! Our design team will call within 24 hours.");
      setLocked(true);
      setShowForm(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const saveToShortlist = async () => {
    if (!customer) {
      toast.info("Sign in to save this design to your Studio", {
        action: { label: "Sign in", onClick: () => window.open("/studio", "_self") },
      });
      return;
    }
    setSaving(true);
    try {
      const cfg = currentConfig();
      await studioApi.post("/studio/shortlist", {
        label: `${cfg.layout} · ${cabinet.label}`,
        config: cfg,
      });
      trackEvent("configurator_saved", { event_category: "3d_configurator", total_inr: bom.total });
      setSavedNow(true);
      toast.success("Saved to your Studio shortlist");
      setTimeout(() => setSavedNow(false), 3500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main data-testid="configurator-page" className="pt-32 pb-24 bg-[#F9F8F6] relative z-10">
      <Seo
        title="3D Modular Kitchen Configurator Kolkata | Live BOM — ZEBO"
        description="Design your dream modular kitchen in real-time 3D. Swap Italian marble, quartz, wood veneer and more — see live pricing and lock your exact design with ZEBO India Pvt Ltd."
        path="/configurator"
      />

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-[#8B0000] mb-4 flex items-center gap-2"><Sparkles size={14} /> Real-time 3D · Live BOM</p>
          <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#143D63] leading-[0.98] max-w-3xl">
            Design your modular kitchen in 3D.
          </h1>
          <p className="text-[#143D63]/70 mt-5 max-w-xl">
            Drag to orbit. Swap countertops, cabinets, flooring and walls on the fly. Prices update live — and when you love the look, lock it and our design team takes over.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-8 mt-12">
          {/* 3D Canvas */}
          <div className="lg:col-span-8 order-2 lg:order-1">
            <div className="border border-[#143D63]/15 bg-[#0F1D2E] aspect-[4/3] relative overflow-hidden">
              <Canvas
                shadows="soft"
                dpr={[1, 2]}
                camera={{ position: [3.4, 2.5, 4.2], fov: 40 }}
                gl={{
                  antialias: true,
                  toneMapping: THREE.ACESFilmicToneMapping,
                  toneMappingExposure: 1.05,
                  outputColorSpace: THREE.SRGBColorSpace,
                }}
                data-testid="configurator-canvas"
              >
                <color attach="background" args={["#0E1926"]} />
                <SoftShadows size={22} focus={0.6} samples={12} />
                <ambientLight intensity={0.25} />
                <directionalLight
                  position={[5, 7, 4]}
                  intensity={1.4}
                  castShadow
                  shadow-mapSize-width={2048}
                  shadow-mapSize-height={2048}
                  shadow-camera-left={-8}
                  shadow-camera-right={8}
                  shadow-camera-top={8}
                  shadow-camera-bottom={-4}
                  shadow-bias={-0.0005}
                />
                <directionalLight position={[-4, 3, -2]} intensity={0.35} color="#E8D9C0" />
                <Suspense fallback={<Html center><span className="text-[#F8C21C] text-sm">Loading 3D…</span></Html>}>
                  <KitchenScene
                    layout={layout}
                    length={length}
                    width={width}
                    cabinetColor={cabinet.color}
                    cabinetId={cabinet.id}
                    countertopColor={countertop.color}
                    countertopId={countertop.id}
                    floorColor={floor.color}
                    floorId={floor.id}
                    wallColor={wall.color}
                    wallId={wall.id}
                    sceneRef={sceneRef}
                  />
                  <Environment preset="apartment" background={false} environmentIntensity={0.8} />
                </Suspense>
                <OrbitControls
                  enablePan={false}
                  minDistance={3}
                  maxDistance={9}
                  minPolarAngle={0.3}
                  maxPolarAngle={Math.PI / 2 - 0.1}
                  target={[0, 1, -0.3]}
                  makeDefault
                />
              </Canvas>

              <div className="absolute top-4 left-4 text-[11px] tracking-[0.15em] font-bold uppercase text-[#F8C21C] bg-[#0A0A0A]/50 backdrop-blur-md px-3 py-1.5">
                Live 3D · Photoreal
              </div>
              <div className="absolute bottom-4 left-4 text-[11px] text-[#F9F8F6]/60 bg-[#0A0A0A]/40 backdrop-blur-md px-3 py-1.5">
                Drag to orbit · Scroll to zoom
              </div>
              <button
                onClick={openInAR}
                data-testid="ar-view-btn"
                className="absolute top-4 right-4 inline-flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-4 py-2 text-xs font-bold hover:bg-[#F9F8F6] transition-colors shadow-lg"
                title="View your kitchen in AR (works best on mobile)"
              >
                <Smartphone size={13} /> View in AR
              </button>
            </div>

            {/* Layout / dimensions */}
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              <div className="border border-[#143D63]/15 bg-white p-4">
                <p className="overline text-[#143D63]/60 mb-2">Layout</p>
                <div className="flex flex-wrap gap-1.5">
                  {LAYOUTS.map((l) => (
                    <button
                      key={l.id}
                      data-testid={`layout-${l.id}`}
                      onClick={() => setLayout(l.id)}
                      className={`text-xs px-3 py-1.5 border ${layout === l.id ? "bg-[#143D63] text-[#F9F8F6] border-[#143D63]" : "text-[#143D63] border-[#143D63]/20 hover:border-[#143D63]"}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="border border-[#143D63]/15 bg-white p-4">
                <p className="overline text-[#143D63]/60 mb-2 flex items-center gap-1"><Ruler size={12} /> Length: {length} ft</p>
                <input
                  data-testid="length-slider"
                  type="range" min={8} max={20} step={1}
                  value={length}
                  onChange={(e) => setLength(parseInt(e.target.value, 10))}
                  className="w-full accent-[#F8C21C]"
                />
              </div>
              <div className="border border-[#143D63]/15 bg-white p-4">
                <p className="overline text-[#143D63]/60 mb-2 flex items-center gap-1"><Ruler size={12} /> Width: {width} ft</p>
                <input
                  data-testid="width-slider"
                  type="range" min={7} max={15} step={1}
                  value={width}
                  onChange={(e) => setWidth(parseInt(e.target.value, 10))}
                  className="w-full accent-[#F8C21C]"
                />
              </div>
            </div>
          </div>

          {/* Right rail: finishes + BOM */}
          <div className="lg:col-span-4 order-1 lg:order-2 space-y-4">
            <div className="border border-[#143D63]/15 bg-white p-5">
              <p className="overline text-[#143D63]/60 mb-3 flex items-center gap-1"><Palette size={12} /> Cabinets</p>
              <div className="grid grid-cols-1 gap-1.5">
                {CABINETS.map((c) => (
                  <Swatch key={c.id} item={c} active={cabinet.id === c.id} onClick={() => setCabinet(c)} />
                ))}
              </div>
            </div>
            <div className="border border-[#143D63]/15 bg-white p-5">
              <p className="overline text-[#143D63]/60 mb-3 flex items-center gap-1"><Palette size={12} /> Countertop</p>
              <div className="grid grid-cols-1 gap-1.5">
                {COUNTERTOPS.map((c) => (
                  <Swatch key={c.id} item={c} active={countertop.id === c.id} onClick={() => setCountertop(c)} />
                ))}
              </div>
            </div>
            <div className="border border-[#143D63]/15 bg-white p-5">
              <p className="overline text-[#143D63]/60 mb-3 flex items-center gap-1"><Palette size={12} /> Flooring</p>
              <div className="grid grid-cols-1 gap-1.5">
                {FLOORS.map((c) => (
                  <Swatch key={c.id} item={c} active={floor.id === c.id} onClick={() => setFloor(c)} />
                ))}
              </div>
            </div>
            <div className="border border-[#143D63]/15 bg-white p-5">
              <p className="overline text-[#143D63]/60 mb-3 flex items-center gap-1"><Palette size={12} /> Wall / Backsplash</p>
              <div className="grid grid-cols-1 gap-1.5">
                {WALLS.map((c) => (
                  <Swatch key={c.id} item={c} active={wall.id === c.id} onClick={() => setWall(c)} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Live BOM */}
        <div className="mt-14 grid lg:grid-cols-12 gap-6 items-stretch">
          <div className="lg:col-span-8 border border-[#143D63]/15 bg-white p-8">
            <p className="overline text-[#8B0000] mb-3">Bill of Materials · Live estimate</p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div className="flex justify-between border-b border-[#143D63]/10 py-2">
                <span className="text-[#143D63]/70">Cabinets — {cabinet.label} ({bom.runningFt} rft)</span>
                <span className="font-bold text-[#143D63]">{formatINR(bom.cabinetsCost)}</span>
              </div>
              <div className="flex justify-between border-b border-[#143D63]/10 py-2">
                <span className="text-[#143D63]/70">Countertop — {countertop.label}</span>
                <span className="font-bold text-[#143D63]">{formatINR(bom.countertopCost)}</span>
              </div>
              <div className="flex justify-between border-b border-[#143D63]/10 py-2">
                <span className="text-[#143D63]/70">Flooring — {floor.label} ({bom.floorSqft} sqft)</span>
                <span className="font-bold text-[#143D63]">{formatINR(bom.floorCost)}</span>
              </div>
              <div className="flex justify-between border-b border-[#143D63]/10 py-2">
                <span className="text-[#143D63]/70">Wall / Backsplash — {wall.label}</span>
                <span className="font-bold text-[#143D63]">{formatINR(bom.wallCost)}</span>
              </div>
              <div className="flex justify-between border-b border-[#143D63]/10 py-2 sm:col-span-2">
                <span className="text-[#143D63]/70">Installation, hardware & finishing (12%)</span>
                <span className="font-bold text-[#143D63]">{formatINR(bom.install)}</span>
              </div>
            </div>
            <p className="text-[11px] text-[#143D63]/50 mt-4">Indicative pricing based on standard Kolkata rates. Final BOQ issued after a site visit.</p>
          </div>
          <motion.div
            layout
            className="lg:col-span-4 bg-gradient-to-br from-[#143D63] to-[#0F2E4C] text-[#F9F8F6] p-8 flex flex-col justify-between"
          >
            <div>
              <p className="overline text-[#F8C21C] mb-2">Estimated total</p>
              <p data-testid="bom-total" className="font-serif text-4xl sm:text-5xl leading-none">
                {formatINR(bom.total)}
              </p>
              <p className="text-[#F9F8F6]/60 text-xs mt-2">Design + installation, all-inclusive.</p>
            </div>
            {locked ? (
              <div className="mt-6 bg-[#F8C21C] text-[#143D63] px-5 py-4 font-bold text-sm flex items-center gap-2">
                <Check size={16} /> Design locked. Team will call within 24h.
              </div>
            ) : (
              <>
                <button
                  data-testid="lock-inquire-btn"
                  onClick={() => setShowForm(true)}
                  className="group mt-6 inline-flex items-center justify-center gap-3 bg-[#F8C21C] text-[#143D63] px-6 py-4 font-bold text-sm hover:bg-[#F9F8F6] transition-colors"
                >
                  <Sparkles size={16} /> Lock &amp; Inquire
                </button>
                <button
                  data-testid="save-shortlist-btn"
                  onClick={saveToShortlist}
                  disabled={saving}
                  className={`mt-2 inline-flex items-center justify-center gap-2 border px-6 py-3 font-bold text-xs transition-colors disabled:opacity-60 ${
                    savedNow
                      ? "bg-green-700 text-white border-green-700"
                      : "border-[#F8C21C]/60 text-[#F8C21C] hover:bg-[#F8C21C] hover:text-[#143D63]"
                  }`}
                >
                  {saving ? <Loader2 size={13} className="animate-spin" /> :
                    savedNow ? <><Check size={13} /> Saved to Studio</> :
                    customer ? <><Bookmark size={13} /> Save to Shortlist</> :
                    <><LogIn size={13} /> Sign in to save</>}
                </button>
              </>
            )}
          </motion.div>
        </div>
      </div>

      {/* SEO content block — explains the tool + long-tail keywords for AEO */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 mt-24">
        <div className="border-t border-[#143D63]/15 pt-14 space-y-10">
          <div>
            <p className="overline text-[#8B0000] mb-3">How the 3D configurator works</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#143D63] font-light leading-tight mb-4">
              Real modular kitchen design, priced in real time.
            </h2>
            <p className="text-[#143D63]/75 leading-relaxed">
              Our browser-based 3D kitchen configurator is engineered specifically for Kolkata homes. Choose the footprint that fits
              your floor plan — L-shape for compact apartments in Rajdanga or Kasba, U-shape for large family kitchens in Ballygunge
              or New Town, or Parallel &amp; Island layouts for open-plan flats in Salt Lake and Alipore. Adjust length and width in
              feet using the sliders, and the entire kitchen re-renders instantly. Every element you see — base cabinets, wall units,
              countertops, splashback, flooring — is a real product we manufacture in our own factory using BWR plywood, HDHMR or
              marine ply carcasses paired with Hettich or Blum hardware.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-[#143D63] mb-3">Finishes you can pick from</h2>
            <ul className="text-[#143D63]/80 leading-relaxed list-disc pl-5 space-y-1.5 text-[15px]">
              <li><b>Countertops:</b> Italian marble (Statuario, Calacatta), Carrara white, quartz (Caesarstone / Kalinga), Kashmir granite, DuPont Corian, and oak butcher-block for warmer kitchens.</li>
              <li><b>Cabinets:</b> handleless white lacquer, walnut natural veneer, matt graphite PU, sage-green lacquer for a modern-Indian look, and champagne shaker doors.</li>
              <li><b>Flooring:</b> wide-plank engineered oak, Italian porcelain, grey Emperador marble, or classic walnut parquet.</li>
              <li><b>Wall &amp; splashback:</b> warm limewash, cool mist paint, clay terracotta plaster, or brass panelling for high-end kitchens.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-[#143D63] mb-3">Live Bill of Materials, no salesman</h2>
            <p className="text-[#143D63]/75 leading-relaxed">
              Unlike traditional interior firms, you do not have to wait for a designer to send a quote. Every time you swap a finish
              or resize the kitchen, our BOM engine recalculates cabinet running-foot cost, countertop square-footage cost, flooring
              area, wall coverage, plus installation, hardware and finishing (12% of joinery). The final number you see is what you
              will actually pay — no hidden surprises. Once you love the design, click <b>Lock &amp; Inquire</b> and our senior designers
              will call within 24 hours to schedule a free site visit, take exact measurements, and issue a signed BOQ.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-[#143D63] mb-3">Serving kitchens across Kolkata</h2>
            <p className="text-[#143D63]/75 leading-relaxed">
              ZEBO India Private Limited delivers modular kitchens end-to-end in Rajdanga (our Chambers Mall studio), Kasba, Salt Lake,
              New Town, Ballygunge, Behala, Garia, Tollygunge, Park Street, Alipore and Howrah — with 10-year carcass warranty and 5-year
              hardware warranty. If you already have an architectural floor plan, upload it directly in <a href="/ai-studio" className="text-[#8B0000] font-semibold link-underline">AI Studio</a> for a full
              photorealistic makeover, or browse completed projects in our <a href="/gallery" className="text-[#8B0000] font-semibold link-underline">Gallery</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Lock & Inquire Modal */}
      {showForm && !locked && (
        <div
          data-testid="lock-modal"
          className="fixed inset-0 z-[60] bg-[#0A0A0A]/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowForm(false)}
        >
          <motion.form
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={lockAndInquire}
            className="bg-[#F9F8F6] w-full max-w-md p-8 relative"
          >
            <p className="overline text-[#8B0000] mb-2">Lock your design</p>
            <h3 className="font-serif text-3xl text-[#143D63] mb-1">Almost yours.</h3>
            <p className="text-[#143D63]/70 text-sm mb-6">We'll call within 24 hours with your final BOQ.</p>
            <div className="space-y-3">
              <input
                data-testid="lock-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none"
              />
              <input
                data-testid="lock-email"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none"
              />
              <input
                data-testid="lock-phone"
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone (WhatsApp preferred)"
                className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none"
              />
              <input
                data-testid="lock-city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="City"
                className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none"
              />
            </div>
            <div className="mt-4 bg-white border border-[#143D63]/10 p-3 text-[12px] text-[#143D63]/80">
              <p><b>Locked design:</b> {LAYOUTS.find((l) => l.id === layout)?.label} · {length}×{width} ft</p>
              <p>{cabinet.label} · {countertop.label} · {floor.label}</p>
              <p className="mt-1 font-bold text-[#8B0000]">Est. {formatINR(bom.total)}</p>
            </div>
            <button
              data-testid="lock-submit"
              disabled={submitting}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#8B0000] text-[#F9F8F6] px-6 py-4 font-bold text-sm hover:bg-[#143D63] transition-colors disabled:opacity-60"
            >
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Locking…</> : <><Send size={15} /> Lock &amp; send to design team</>}
            </button>
          </motion.form>
        </div>
      )}

      {/* AR "View in Your Space" modal — uses Google's model-viewer web component */}
      {arOpen && (
        <div
          className="fixed inset-0 z-[70] bg-[#0A0A0A]/85 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="ar-modal"
          onClick={() => setArOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0A0A0A] border border-[#F8C21C]/30">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#F8C21C]/20">
              <div className="flex items-center gap-2 text-[#F9F8F6]">
                <Cuboid size={16} className="text-[#F8C21C]" />
                <span className="font-serif text-lg">Your kitchen · AR preview</span>
              </div>
              <button onClick={() => setArOpen(false)} data-testid="ar-close" className="text-[#F9F8F6]/80 hover:text-[#F8C21C]">✕</button>
            </div>
            <div className="aspect-[4/3] relative">
              {arUrl && (
                <model-viewer
                  src={arUrl}
                  ar
                  ar-modes="webxr scene-viewer quick-look"
                  camera-controls
                  auto-rotate
                  shadow-intensity="1"
                  exposure="1.05"
                  environment-image="neutral"
                  style={{ width: "100%", height: "100%", background: "#0A0A0A" }}
                >
                  <button
                    slot="ar-button"
                    data-testid="ar-launch-btn"
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[#F8C21C] text-[#143D63] px-5 py-3 font-bold text-sm inline-flex items-center gap-2"
                  >
                    <Smartphone size={14} /> View in your room
                  </button>
                </model-viewer>
              )}
            </div>
            <div className="p-4 text-[11px] text-[#F9F8F6]/70 space-y-1">
              <p><b className="text-[#F8C21C]">On mobile:</b> Tap "View in your room" to place this kitchen on your floor (Android Scene Viewer / iOS Quick Look).</p>
              <p><b className="text-[#F8C21C]">On desktop:</b> Drag to orbit · scroll to zoom · a preview of what your customer will see on their phone.</p>
            </div>
          </div>
        </div>
      )}

      {arBuilding && (
        <div className="fixed inset-0 z-[75] bg-[#0A0A0A]/70 flex items-center justify-center pointer-events-none">
          <div className="bg-[#143D63] text-[#F9F8F6] px-6 py-4 flex items-center gap-3">
            <Loader2 className="animate-spin text-[#F8C21C]" size={18} /> Building AR model…
          </div>
        </div>
      )}
    </main>
  );
}
